import React from "react";
import { ExternalLink, Star } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export default function ProductCard({ product, index = 0 }) {
  const { L, t } = useI18n();
  const name = L(product, "name") || product.name;
  const match = product.match;

  return (
    <div
      className="group bg-white border border-rose-100 rounded-xl overflow-hidden hover-lift flex flex-col animate-fade-up opacity-0"
      style={{ animationDelay: `${(index % 12) * 45}ms` }}
      data-testid={`product-card-${product.sku}`}
    >
      <div className="relative aspect-square overflow-hidden bg-ivory flex items-center justify-center">
        {product.image ? (
          <img
            src={product.image}
            alt={name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "block"; }}
          />
        ) : null}
        <div
          className="w-24 h-24 rounded-full shadow-inner transition-transform duration-500 group-hover:scale-110"
          style={{ background: product.color_hex || "#EAD9CE", boxShadow: "inset 0 -8px 20px rgba(0,0,0,0.12)", display: product.image ? "none" : "block" }}
        />
        {product.color_hex && product.domain !== "skincare" && (
          <span className="absolute bottom-2 left-2 w-6 h-6 rounded-full border-2 border-white shadow" style={{ background: product.color_hex }} title={product.shade || product.color} />
        )}
        {typeof match === "number" && (
          <span className="absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full text-white" style={{ background: match >= 90 ? "#C9A45E" : "#DDA7A5" }} data-testid={`match-${product.sku}`}>
            {match}% {t("common.match")}
          </span>
        )}
        {product.price_tier && (() => {
          const map = { "$": ["Economico", "Budget", "bg-olive/15 text-olive-dark"], "$$": ["Medio", "Mid", "bg-gold/20 text-gold-dark"], "$$$": ["Lusso", "Luxury", "bg-charcoal text-white"] };
          const [it_, en_, cls] = map[product.price_tier] || map["$$"];
          return <span className={`absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-1 ${cls}`} data-testid={`tier-badge-${product.sku}`}>{L({ label_it: it_, label_en: en_ }, "label")}</span>;
        })()}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-widest text-rose-500">{product.brand}</span>
          {product.rating && (
            <span className="flex items-center gap-0.5 text-[11px] text-charcoal/60"><Star size={12} className="fill-gold text-gold" />{product.rating}</span>
          )}
        </div>
        <h3 className="mt-1 font-serif text-base leading-snug text-charcoal line-clamp-2">{name}</h3>
        {(product.finish || product.style || product.skin_type) && (
          <p className="mt-1 text-xs text-charcoal/50">{product.finish || product.style || product.skin_type}</p>
        )}
        <div className="mt-auto pt-3 flex items-center justify-between">
          <span className="font-semibold text-charcoal">€{Number(product.price).toFixed(2)}</span>
          <a
            href={product.affiliate_url || "#"}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="inline-flex items-center gap-1 text-xs font-semibold bg-charcoal text-white rounded-full px-3.5 py-2 hover:bg-rose-500 transition-colors"
            data-testid={`buy-${product.sku}`}
          >
            {t("common.buy")} <ExternalLink size={13} />
          </a>
        </div>
      </div>
    </div>
  );
}
